/**
 * Supabase authentication over the REST API.
 *
 * No SDK: the two calls this product needs — send a magic link, exchange the
 * session — are one `fetch` each, and adding a client library for that would
 * put a large dependency on the critical path of an app whose whole point is
 * loading fast.
 *
 * Only the *publishable* anon key is used here. It is designed to be public;
 * it grants nothing on its own, because every table is behind RLS and the
 * subscription row is read-only even to its owner.
 */

import { UPGRADE_DESTINATION } from '@/config/subscription';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL ?? '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';

/** Auth is optional: without configuration the app runs exactly as before. */
export const isAuthConfigured = (): boolean =>
  SUPABASE_URL.length > 0 && SUPABASE_ANON_KEY.length > 0;

export interface AuthSession {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  email: string | null;
  userId: string;
  /**
   * When Supabase confirmed `email` — the user's `email_confirmed_at`, as
   * `/auth/v1/user` reports it. Null while Supabase has not confirmed it, and
   * absent on a session stored before this was recorded; either way the address
   * is not treated as verified. See `verifiedEmail`.
   */
  emailConfirmedAt?: string | null;
}

const STORAGE_KEY = 'quotylab:auth';

export function readSession(): AuthSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as AuthSession;
    return session.accessToken && session.userId ? session : null;
  } catch {
    return null;
  }
}

function writeSession(session: AuthSession | null): void {
  try {
    if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private mode — the session simply will not survive a reload */
  }
}

interface TokenResponse {
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  user?: { id?: string; email?: string };
  error_description?: string;
  msg?: string;
}

function toSession(payload: TokenResponse): AuthSession | null {
  if (!payload.access_token || !payload.user?.id) return null;
  return {
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token ?? '',
    expiresAt: Date.now() + (payload.expires_in ?? 3600) * 1000,
    email: payload.user.email ?? null,
    userId: payload.user.id,
  };
}

/**
 * Where a magic link returns to: this site's Plan card in Settings. Sign-in is
 * only offered on that card, so it is where the user was.
 *
 * Before this, no `redirect_to` was sent. Supabase then falls back to the
 * `Referer` header, which the site's `strict-origin-when-cross-origin` policy
 * trims to the bare origin — so every sign-in landed on the homepage.
 *
 * The URL is this page's own origin plus a fixed path. Nothing in the current
 * URL, the query string or the email request can change it, so it cannot be
 * turned into an open redirect. Supabase checks it again and honours it only if
 * its scheme, host and port match the project's Site URL; otherwise it falls
 * back to the Site URL, exactly as before.
 *
 * It carries no business id. The active business is already persisted on this
 * device, and an id in a URL would be input to distrust, not context to keep.
 */
export function signInReturnUrl(origin: string = window.location.origin): string {
  return `${origin}${UPGRADE_DESTINATION.pathname}${UPGRADE_DESTINATION.search}`;
}

/** Sends a one-time sign-in link. No password to store, lose or leak. */
export async function sendMagicLink(email: string): Promise<void> {
  const returnTo = encodeURIComponent(signInReturnUrl());
  const response = await fetch(`${SUPABASE_URL}/auth/v1/otp?redirect_to=${returnTo}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', apikey: SUPABASE_ANON_KEY },
    body: JSON.stringify({ email, create_user: true }),
  });
  if (!response.ok) throw new Error('Could not send the sign-in link. Try again in a moment.');
}

/**
 * Completes sign-in from the callback fragment Supabase appends to the URL.
 *
 * The tokens arrive in the hash rather than the query string, so they are never
 * sent to the server or written to any log.
 */
export function completeSignInFromUrl(): AuthSession | null {
  if (!window.location.hash.includes('access_token')) return null;

  const params = new URLSearchParams(window.location.hash.slice(1));
  const session = toSession({
    access_token: params.get('access_token') ?? undefined,
    refresh_token: params.get('refresh_token') ?? undefined,
    expires_in: Number(params.get('expires_in') ?? 3600),
    user: { id: params.get('user_id') ?? undefined },
  });

  // The fragment carries no user id, so the session is confirmed against the
  // server before it is trusted — see `hydrateSession`.
  const accessToken = params.get('access_token');
  if (accessToken) {
    history.replaceState(null, '', window.location.pathname + window.location.search);
    return { accessToken, refreshToken: params.get('refresh_token') ?? '',
      expiresAt: Date.now() + Number(params.get('expires_in') ?? 3600) * 1000,
      email: null, userId: 'pending' };
  }
  return session;
}

/**
 * Where restoring a session stands.
 *
 * `restoring` exists so the UI never has to guess. A stored session takes one
 * round trip to confirm, and rendering "signed out" during that trip is how a
 * signed-in customer sees a sign-in form on every page load.
 */
export type AuthStatus = 'restoring' | 'signed-in' | 'signed-out';

/** Whether this page load is the landing of a magic link. Read, never consumed. */
export function hasPendingSignIn(): boolean {
  try {
    return window.location.hash.includes('access_token');
  } catch {
    return false;
  }
}

/**
 * What the account UI may show before anything has been confirmed.
 *
 * Signed out only when there is genuinely nothing to restore — no stored
 * session and no magic link arriving. Anything else is `restoring` until
 * Supabase has answered.
 */
export function initialAuthStatus(
  configured: boolean, pendingSignIn: boolean, stored: AuthSession | null,
): AuthStatus {
  return configured && (pendingSignIn || stored !== null) ? 'restoring' : 'signed-out';
}

/**
 * The outcome of asking Supabase about a session.
 *
 *   confirmed    — Supabase vouched for it; it is stored and may be used.
 *   rejected     — Supabase answered that it is over (revoked, expired beyond
 *                  refresh). Storage is cleared. This is the only outcome that
 *                  signs anyone out.
 *   unreachable  — no answer: offline, DNS, a 5xx. Nothing was decided, so
 *                  nothing is cleared.
 */
export type HydrateResult =
  | { status: 'confirmed'; session: AuthSession }
  | { status: 'rejected' }
  | { status: 'unreachable' };

/** Renew a token this long before it expires rather than send a dying one. */
export const REFRESH_MARGIN_MS = 60_000;

/** Supabase's answer that a credential is not accepted — as opposed to an outage. */
const isRefusal = (status: number): boolean => status === 400 || status === 401 || status === 403;

const REJECTED: HydrateResult = { status: 'rejected' };
const UNREACHABLE: HydrateResult = { status: 'unreachable' };

/**
 * Confirms a session with Supabase and fills in who it belongs to.
 *
 * An access token lives for an hour; the refresh token stored beside it is how
 * a session outlives that. Before this, an expired token was treated as a
 * signed-out user and the refresh token was thrown away with it — so anyone
 * returning after an hour, including from checkout, was signed out while their
 * cached plan still said Pro. Now an expired or refused token is renewed first,
 * and the session is cleared only when Supabase refuses the renewal too.
 */
export async function hydrateSession(session: AuthSession): Promise<HydrateResult> {
  if (session.refreshToken && session.expiresAt - Date.now() <= REFRESH_MARGIN_MS) {
    return refreshSession(session);
  }
  return confirm(session, true);
}

/**
 * Exchanges the refresh token for a new session — Supabase's own renewal,
 * `POST /auth/v1/token?grant_type=refresh_token`.
 *
 * Refresh tokens rotate: each one is single-use, so the new one is stored the
 * moment it arrives. The identity is then confirmed with `/auth/v1/user` rather
 * than read from the renewal response, exactly as a fresh sign-in is.
 */
export async function refreshSession(session: AuthSession): Promise<HydrateResult> {
  if (!session.refreshToken) {
    writeSession(null);
    return REJECTED;
  }

  let response: Response;
  try {
    response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', apikey: SUPABASE_ANON_KEY },
      body: JSON.stringify({ refresh_token: session.refreshToken }),
    });
  } catch {
    return UNREACHABLE;
  }

  if (isRefusal(response.status)) {
    writeSession(null);
    return REJECTED;
  }
  if (!response.ok) return UNREACHABLE;

  const payload = (await response.json().catch(() => ({}))) as TokenResponse;
  if (!payload.access_token) return UNREACHABLE;

  return confirm({
    ...session,
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token ?? session.refreshToken,
    expiresAt: Date.now() + (payload.expires_in ?? 3600) * 1000,
  }, false);
}

async function confirm(session: AuthSession, mayRefresh: boolean): Promise<HydrateResult> {
  let response: Response;
  try {
    response = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
      headers: { authorization: `Bearer ${session.accessToken}`, apikey: SUPABASE_ANON_KEY },
    });
  } catch {
    return UNREACHABLE;
  }

  if (isRefusal(response.status)) {
    // A token refused before its stated expiry — clock skew, or revoked early.
    // The refresh token may still be good; it is tried once, never in a loop.
    if (mayRefresh && session.refreshToken) return refreshSession(session);
    writeSession(null);
    return REJECTED;
  }
  if (!response.ok) return UNREACHABLE;

  const user = (await response.json().catch(() => ({}))) as {
    id?: string; email?: string; email_confirmed_at?: string | null;
  };
  if (!user.id) return UNREACHABLE;

  const confirmed: AuthSession = {
    ...session, userId: user.id, email: user.email ?? null,
    emailConfirmedAt: user.email_confirmed_at ?? null,
  };
  writeSession(confirmed);
  return { status: 'confirmed', session: confirmed };
}

/**
 * The session's email, only if Supabase has confirmed it — otherwise null.
 *
 * An address alone proves nothing: Supabase can hold an account whose email was
 * never confirmed. The proof is `email_confirmed_at`, recorded by `confirm` from
 * `/auth/v1/user` each time the session is restored or renewed. A session with
 * no record of it — including one stored before it was recorded — answers null
 * until Supabase has been asked again.
 */
export function verifiedEmail(session: AuthSession | null): string | null {
  if (!session?.email || session.userId === 'pending') return null;
  const confirmedAt = session.emailConfirmedAt;
  return typeof confirmedAt === 'string' && !Number.isNaN(Date.parse(confirmedAt)) ? session.email : null;
}

/**
 * Ends the session on this device — and at Supabase.
 *
 * Clearing local storage alone left the refresh token valid at Supabase, so a
 * copy of it could go on minting sessions, and calling the billing API, after
 * "sign out". `POST /auth/v1/logout` revokes it; `scope=local` ends this
 * session only, not the account's sessions on other devices.
 *
 * Best-effort, and never awaited by the UI: the device is signed out either
 * way. Access tokens are stateless JWTs and remain valid until they expire —
 * an hour at most — which is Supabase's own model and not something a client
 * can shorten.
 */
export function signOut(session?: AuthSession | null): void {
  writeSession(null);
  if (!session?.accessToken) return;
  void fetch(`${SUPABASE_URL}/auth/v1/logout?scope=local`, {
    method: 'POST',
    headers: { authorization: `Bearer ${session.accessToken}`, apikey: SUPABASE_ANON_KEY },
    keepalive: true,
  }).catch(() => undefined);
}
